import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Button, Typography, Slider, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Chip,
  TextField, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import QRCodeStyling from 'qr-code-styling';
import {
  listQrPresets,
  getQrPreset,
  createQrPreset,
  updateQrPreset,
  deleteQrPreset,
  uploadQrLogo,
  deleteQrLogo,
} from '../../Actions';
import { AlertContext } from '../../Context/AlertContextProvider';
import { baseUrl } from '../../Contants';

const PREVIEW_URL = 'https://showroompricing.com/product/preview';
const PREVIEW_SIZE = 160;
const SWATCH_SIZE = 28;

const DOT_TYPES = [
  { value: 'square', label: 'Square' },
  { value: 'dots', label: 'Dots' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
];

const CORNER_SQUARE_TYPES = [
  { value: 'square', label: 'Square' },
  { value: 'extra-rounded', label: 'Rounded' },
  { value: 'dot', label: 'Dot' },
];

const CORNER_DOT_TYPES = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
];

// Fields the user can place on label lines — keys must match server LABEL_FIELD_RESOLVERS
const LABEL_FIELDS = [
  { key: 'trade_name',   label: 'Trade Name' },
  { key: 'style',        label: 'Style' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'material',     label: 'Material' },
  { key: 'price',        label: 'Price' },
];

// null = SF/SY normalized catch-all; others = exact UOM raw price
const UOM_OPTIONS = [
  { value: '', label: 'SF / SY (sq ft)' },
  { value: 'LF', label: 'LF — Linear Foot' },
  { value: 'LY', label: 'LY — Linear Yard' },
  { value: 'EA', label: 'EA — Each / Piece' },
  { value: 'PC', label: 'PC — Piece' },
  { value: 'BX', label: 'BX — Box' },
  { value: 'SY', label: 'SY — Sq Yard (raw)' },
  { value: 'SF', label: 'SF — Sq Foot (raw)' },
];

const FACTORY = {
  fg_color: '#000000', bg_color: '#FFFFFF', logo_s3_key: null, logo_size_pct: 20,
  dot_type: 'square', corner_square_type: 'square', corner_dot_type: 'square',
  label_line1: 'trade_name,style', label_line2: '',
};

const csvToArray = (csv) => csv ? csv.split(',').map((s) => s.trim()).filter(Boolean) : [];
const arrayToCsv = (arr) => arr.join(',');
const toggleField = (arr, key) =>
  arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];

const emptyRule = () => ({
  rule_name: '',
  min_price_sqft: '',
  max_price_sqft: '',
  fg_color: '#000000',
  bg_color: '#FFFFFF',
  uom_type_cd: '',
});

// ── Label line chip selector ──────────────────────────────────────────────────

const LabelLineSelector = ({ label, fields, onChange }) => (
  <div>
    <Typography variant="caption" display="block" gutterBottom>{label}</Typography>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {LABEL_FIELDS.map(({ key, label: chipLabel }) => {
        const active = fields.includes(key);
        return (
          <Chip
            key={key}
            label={chipLabel}
            size="small"
            onClick={() => onChange(toggleField(fields, key))}
            style={{
              background: active ? '#4E564E' : '#EEEEEE',
              color: active ? '#fff' : '#333',
              cursor: 'pointer',
              fontWeight: active ? 600 : 400,
            }}
          />
        );
      })}
    </div>
    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
      {fields.length ? fields.map((k) => LABEL_FIELDS.find((f) => f.key === k)?.label).filter(Boolean).join(' · ') : 'Hidden'}
    </Typography>
  </div>
);

// ── Live preview ──────────────────────────────────────────────────────────────

const QrPreview = ({ fgColor, bgColor, dotType, cornerSquareType, cornerDotType, logoSrc, logoSizePct, line1Text, line2Text }) => {
  const containerRef = useRef(null);
  const qrRef = useRef(null);

  const getOptions = () => ({
    width: PREVIEW_SIZE,
    height: PREVIEW_SIZE,
    data: PREVIEW_URL,
    dotsOptions: { color: fgColor, type: dotType },
    cornersSquareOptions: { color: fgColor, type: cornerSquareType },
    cornersDotOptions: { color: fgColor, type: cornerDotType },
    backgroundOptions: { color: bgColor },
    image: logoSrc || undefined,
    imageOptions: {
      margin: 4,
      hideBackgroundDots: true,
      imageSize: logoSrc ? (logoSizePct / 100) : 0,
    },
    qrOptions: { errorCorrectionLevel: logoSrc ? 'H' : 'M' },
  });

  useEffect(() => {
    if (!containerRef.current) return;
    qrRef.current = new QRCodeStyling(getOptions());
    containerRef.current.innerHTML = '';
    qrRef.current.append(containerRef.current);
  }, []);

  useEffect(() => {
    qrRef.current?.update(getOptions());
  }, [fgColor, bgColor, dotType, cornerSquareType, cornerDotType, logoSrc, logoSizePct]);

  const textStyle = { fontSize: 9, textAlign: 'center', color: '#333', lineHeight: '12px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: PREVIEW_SIZE };

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', background: bgColor, padding: '2px 4px', borderRadius: 4 }}>
      {line1Text && <div style={textStyle}>{line1Text}</div>}
      <div ref={containerRef} />
      {line2Text && <div style={textStyle}>{line2Text}</div>}
    </div>
  );
};

const ColorCell = ({ value, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: SWATCH_SIZE, height: SWATCH_SIZE, cursor: 'pointer', border: 'none', padding: 0, borderRadius: 4 }}
    />
    <Typography variant="caption" style={{ fontFamily: 'monospace', fontSize: 11 }}>{value}</Typography>
  </div>
);

const SAMPLE_DATA = { trade_name: 'Sample Carpet', style: 'AB1234', manufacturer_name: 'Shaw Floors', material_class: 'Carpet', max_price_raw: '3.99', price_uom_type_cd: 'SF' };

const resolvePreviewLine = (fields) =>
  fields
    .map((k) => {
      if (k === 'trade_name') return SAMPLE_DATA.trade_name;
      if (k === 'style') return SAMPLE_DATA.style;
      if (k === 'manufacturer') return SAMPLE_DATA.manufacturer_name;
      if (k === 'material') return SAMPLE_DATA.material_class;
      if (k === 'price') return `$${SAMPLE_DATA.max_price_raw}/${SAMPLE_DATA.price_uom_type_cd}`;
      return null;
    })
    .filter(Boolean)
    .join('  ') || '';

// ── Main component ────────────────────────────────────────────────────────────

const QrStylePresets = () => {
  const { handleAlertOpen, setMessageType, setMessage } = useContext(AlertContext);

  const [presets, setPresets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [presetName, setPresetName] = useState('');

  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [logoS3Key, setLogoS3Key] = useState(null);
  const [logoSizePct, setLogoSizePct] = useState(20);
  const [dotType, setDotType] = useState('square');
  const [cornerSquareType, setCornerSquareType] = useState('square');
  const [cornerDotType, setCornerDotType] = useState('square');
  const [labelLine1, setLabelLine1] = useState(['trade_name', 'style']);
  const [labelLine2, setLabelLine2] = useState([]);

  const [logoPreviewSrc, setLogoPreviewSrc] = useState(null);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);

  const [priceRules, setPriceRules] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const success = (m) => { setMessage(m); setMessageType('success'); handleAlertOpen(); };
  const fail = (m) => { setMessage(m); setMessageType('error'); handleAlertOpen(); };

  const applyAppearance = (d) => {
    setFgColor(d.fg_color || '#000000');
    setBgColor(d.bg_color || '#FFFFFF');
    setLogoS3Key(d.logo_s3_key || null);
    setLogoSizePct(d.logo_size_pct || 20);
    setDotType(d.dot_type || 'square');
    setCornerSquareType(d.corner_square_type || 'square');
    setCornerDotType(d.corner_dot_type || 'square');
    setLabelLine1(csvToArray(d.label_line1 || 'trade_name,style'));
    setLabelLine2(csvToArray(d.label_line2 || ''));
    setPendingLogoFile(null);
  };

  const loadPreset = (id) =>
    getQrPreset(id).then((res) => {
      const d = res.data.data;
      setSelectedId(d.preset_id);
      setPresetName(d.preset_name || '');
      setIsDefault(!!d.is_default);
      applyAppearance(d);
      setLogoPreviewSrc(d.logo_s3_key ? `${baseUrl}/logo/qr_logo?preset_id=${d.preset_id}&t=${Date.now()}` : null);
      setPriceRules((d.price_rules || []).map((r) => ({
        ...r,
        uom_type_cd: r.uom_type_cd || '',
        min_price_sqft: r.min_price_sqft != null ? String(r.min_price_sqft) : '',
        max_price_sqft: r.max_price_sqft != null ? String(r.max_price_sqft) : '',
      })));
    });

  const refreshList = () => listQrPresets().then((res) => setPresets(res.data.data || []));

  useEffect(() => {
    listQrPresets()
      .then((res) => {
        const list = res.data.data || [];
        setPresets(list);
        const def = list.find((p) => p.is_default) || list[0];
        if (def) return loadPreset(def.preset_id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const cleanRules = () => priceRules.map((r) => ({
    rule_name: r.rule_name || '',
    uom_type_cd: r.uom_type_cd || null,
    min_price_sqft: r.min_price_sqft !== '' ? parseFloat(r.min_price_sqft) : null,
    max_price_sqft: r.max_price_sqft !== '' ? parseFloat(r.max_price_sqft) : null,
    fg_color: r.fg_color || '#000000',
    bg_color: r.bg_color || '#FFFFFF',
  }));

  const uploadPendingLogo = async (presetId) => {
    if (!pendingLogoFile) return logoS3Key;
    const formData = new FormData();
    formData.append('logo', pendingLogoFile);
    formData.append('preset_id', presetId);
    const res = await uploadQrLogo(formData);
    return res.data.s3_key;
  };

  const appearancePayload = (s3Key) => ({
    fg_color: fgColor,
    bg_color: bgColor,
    logo_s3_key: s3Key || null,
    logo_size_pct: logoSizePct,
    dot_type: dotType,
    corner_square_type: cornerSquareType,
    corner_dot_type: cornerDotType,
    label_line1: arrayToCsv(labelLine1),
    label_line2: arrayToCsv(labelLine2),
    price_rules: cleanRules(),
  });

  const handleSave = async () => {
    if (!presetName.trim()) { fail('Enter a preset name'); return; }
    setSaving(true);
    try {
      const s3Key = await uploadPendingLogo(selectedId);
      await updateQrPreset({ preset_id: selectedId, preset_name: presetName.trim(), ...appearancePayload(s3Key) });
      setLogoS3Key(s3Key || null);
      setPendingLogoFile(null);
      if (s3Key) setLogoPreviewSrc(`${baseUrl}/logo/qr_logo?preset_id=${selectedId}&t=${Date.now()}`);
      await refreshList();
      success('Preset saved');
    } catch {
      fail('Error saving preset');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAs = async () => {
    const name = presetName.trim() || 'New Preset';
    setSaving(true);
    try {
      const res = await createQrPreset({ preset_name: name, ...appearancePayload(null) });
      const newId = res.data.data.preset_id;
      if (pendingLogoFile) await uploadPendingLogo(newId);
      await refreshList();
      await loadPreset(newId);
      success('Preset created');
    } catch {
      fail('Error creating preset');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isDefault) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete preset "${presetName}"?`)) return;
    setSaving(true);
    try {
      await deleteQrPreset(selectedId);
      const res = await listQrPresets();
      const list = res.data.data || [];
      setPresets(list);
      const def = list.find((p) => p.is_default) || list[0];
      if (def) await loadPreset(def.preset_id);
      success('Preset deleted');
    } catch {
      fail('Error deleting preset');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    applyAppearance(FACTORY);
    setLogoPreviewSrc(null);
    setPriceRules([]);
  };

  const handleSelectPreset = (id) => {
    if (id === selectedId) return;
    loadPreset(id).catch(() => fail('Error loading preset'));
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setPendingLogoFile(file);
    setLogoPreviewSrc(URL.createObjectURL(file));
  };

  const handleRemoveLogo = async () => {
    // Staged but never uploaded — just drop it locally.
    if (pendingLogoFile && !logoS3Key) {
      setPendingLogoFile(null);
      setLogoPreviewSrc(null);
      return;
    }
    try {
      await deleteQrLogo(selectedId);
      setLogoS3Key(null);
      setLogoPreviewSrc(null);
      setPendingLogoFile(null);
      success('Logo removed');
    } catch {
      fail('Error removing logo');
    }
  };

  const updateRule = (index, field, value) =>
    setPriceRules((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  const addRule = () => setPriceRules((prev) => [...prev, emptyRule()]);
  const removeRule = (index) => setPriceRules((prev) => prev.filter((_, i) => i !== index));

  if (loading) return <CircularProgress style={{ margin: 20 }} />;

  const previewLine1 = resolvePreviewLine(labelLine1);
  const previewLine2 = resolvePreviewLine(labelLine2);

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* Preset toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <FormControl size="small" variant="outlined" style={{ minWidth: 200 }}>
          <InputLabel>Preset</InputLabel>
          <Select
            value={selectedId || ''}
            onChange={(e) => handleSelectPreset(e.target.value)}
            label="Preset"
          >
            {presets.map((p) => (
              <MenuItem key={p.preset_id} value={p.preset_id}>
                {p.preset_name}{p.is_default ? ' (Default)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Name"
          size="small"
          variant="outlined"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          style={{ minWidth: 180 }}
        />

        <div style={{ flex: 1 }} />

        <Button variant="contained" style={{ background: '#EEEEEE', color: '#009D47' }}
          onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
        <Button variant="outlined" style={{ borderColor: '#4E564E', color: '#4E564E' }}
          onClick={handleSaveAs} disabled={saving}>
          Save As New
        </Button>
        <Button variant="outlined" style={{ borderColor: '#4E564E', color: '#4E564E' }}
          onClick={handleResetToDefault} disabled={saving}>
          Reset to Default
        </Button>
        <Button variant="outlined" style={{ borderColor: '#ff682c', color: '#ff682c' }}
          onClick={handleDelete} disabled={saving || isDefault}>
          Delete
        </Button>
      </div>

      <Divider />

      {/* Appearance + preview */}
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', justifyContent: 'center', marginTop: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 220, flex: 1 }}>

          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <Typography variant="caption">QR Color</Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <input type="color" value={fgColor} onChange={(e) => setFgColor(e.target.value)}
                  style={{ width: 40, height: 32, cursor: 'pointer', border: 'none', padding: 0 }} />
                <Typography variant="body2">{fgColor}</Typography>
              </div>
            </div>
            <div>
              <Typography variant="caption">Background</Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)}
                  style={{ width: 40, height: 32, cursor: 'pointer', border: 'none', padding: 0 }} />
                <Typography variant="body2">{bgColor}</Typography>
              </div>
            </div>
          </div>

          <FormControl size="small" variant="outlined">
            <InputLabel>Dot Style</InputLabel>
            <Select value={dotType} onChange={(e) => setDotType(e.target.value)} label="Dot Style">
              {DOT_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" variant="outlined">
            <InputLabel>Corner Eye</InputLabel>
            <Select value={cornerSquareType} onChange={(e) => setCornerSquareType(e.target.value)} label="Corner Eye">
              {CORNER_SQUARE_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" variant="outlined">
            <InputLabel>Corner Dot</InputLabel>
            <Select value={cornerDotType} onChange={(e) => setCornerDotType(e.target.value)} label="Corner Dot">
              {CORNER_DOT_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>

          <div>
            <Typography variant="caption">Logo Size: {logoSizePct}%</Typography>
            <Slider value={logoSizePct} min={5} max={30} step={1}
              onChange={(_, v) => setLogoSizePct(v)}
              disabled={!logoPreviewSrc}
              style={{ color: '#4E564E' }} />
          </div>

          <div>
            <Typography variant="caption" display="block" gutterBottom>Logo (optional)</Typography>
            <input type="file" accept="image/png,image/jpeg,image/svg+xml"
              ref={fileInputRef} style={{ display: 'none' }} onChange={handleLogoFileChange} />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="contained" size="small" style={{ background: '#EEEEEE' }}
                onClick={() => fileInputRef.current.click()}>
                {logoPreviewSrc ? 'Change Logo' : 'Upload Logo'}
              </Button>
              {logoPreviewSrc && (
                <Button variant="contained" size="small" style={{ background: '#EEEEEE', color: '#ff682c' }}
                  onClick={handleRemoveLogo}>
                  Remove
                </Button>
              )}
            </div>
          </div>

          <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Typography variant="caption" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
              Label Text
            </Typography>
            <LabelLineSelector label="Line above QR" fields={labelLine1} onChange={setLabelLine1} />
            <LabelLineSelector label="Line below QR" fields={labelLine2} onChange={setLabelLine2} />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <Typography variant="caption">Live Preview</Typography>
          <div style={{ padding: 8, borderRadius: 4, boxShadow: '0 2px 6px rgba(0,0,0,0.15)', display: 'inline-block', background: '#f5f5f5' }}>
            <QrPreview
              fgColor={fgColor}
              bgColor={bgColor}
              dotType={dotType}
              cornerSquareType={cornerSquareType}
              cornerDotType={cornerDotType}
              logoSrc={logoPreviewSrc}
              logoSizePct={logoSizePct}
              line1Text={previewLine1}
              line2Text={previewLine2}
            />
          </div>
          <Typography variant="caption" color="textSecondary">1.5" on printed label</Typography>
        </div>
      </div>

      <Divider style={{ margin: '24px 0 16px' }} />

      {/* Price colors */}
      <Typography variant="caption" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 8 }}>
        Price Colors
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Color QR codes in this preset by the product's highest retail price. For <strong>SF/SY</strong> rules the price
        is normalized to per sq ft (SY ÷ 9). All other UOMs use the raw price as-is.
        UOM-specific rules take priority over SF/SY catch-all rules. Ranges are <em>min ≤ price &lt; max</em>; leave a bound blank for no limit.
      </Typography>

      <div style={{ overflowX: 'auto', marginTop: 12 }}>
        <Table size="small" style={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>UOM</TableCell>
              <TableCell>Min price</TableCell>
              <TableCell>Max price</TableCell>
              <TableCell>QR Color</TableCell>
              <TableCell>Background</TableCell>
              <TableCell style={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {priceRules.map((rule, i) => (
              <TableRow key={i}>
                <TableCell>
                  <TextField
                    value={rule.rule_name}
                    onChange={(e) => updateRule(i, 'rule_name', e.target.value)}
                    placeholder="e.g. Premium"
                    size="small"
                    inputProps={{ style: { fontSize: 13 } }}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={rule.uom_type_cd}
                    onChange={(e) => updateRule(i, 'uom_type_cd', e.target.value)}
                    displayEmpty
                    style={{ fontSize: 13, minWidth: 140 }}
                  >
                    {UOM_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value} style={{ fontSize: 13 }}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    value={rule.min_price_sqft}
                    onChange={(e) => updateRule(i, 'min_price_sqft', e.target.value)}
                    placeholder="0"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 0.01, style: { fontSize: 13, width: 72 } }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={rule.max_price_sqft}
                    onChange={(e) => updateRule(i, 'max_price_sqft', e.target.value)}
                    placeholder="∞"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 0.01, style: { fontSize: 13, width: 72 } }}
                  />
                </TableCell>
                <TableCell>
                  <ColorCell value={rule.fg_color} onChange={(v) => updateRule(i, 'fg_color', v)} />
                </TableCell>
                <TableCell>
                  <ColorCell value={rule.bg_color} onChange={(v) => updateRule(i, 'bg_color', v)} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => removeRule(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {priceRules.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="caption" color="textSecondary">
                    No price rules — every QR in this preset uses the colors above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div style={{ marginTop: 16 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRule}
          style={{ borderColor: '#4E564E', color: '#4E564E' }}
        >
          Add Rule
        </Button>
      </div>

      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 20 }}>
        Changes to colors, logo, label text and price rules are saved together when you click <strong>Save</strong>.
      </Typography>
    </div>
  );
};

export default QrStylePresets;
